'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '../ui/button'
import { SearchCombobox } from '../ui/search'
import { cn } from '@/lib/utils'
import { CalendarCheck2, CalendarX2, Coins } from 'lucide-react'
import { format, parse } from 'date-fns'
import { DatePickerWithRange } from '../booking/DateRangePicker'
import { DateRange } from 'react-day-picker'
import { usePathname } from 'next/navigation'
import { Link } from '@/i18n/navigation'
import GuestSelector from '../booking/GuestSelector'
import { useBookingBarStore } from '@/lib/context/bookingBarContext'
import { useIsMobile } from '@/hooks/use-mobile'
import { City } from '@/payload-types'
import useBookingSearchParams from '@/hooks/useBookingParamsHook'
import { useBookingParams } from '@/hooks/useBookingParams'
import { useTranslations } from 'next-intl'
import PromoCodeInput from '../booking/PromoCodeInput'

export type ActiveHotelRoutes = Record<string, { country: string; city: string; hotel: string }>

const BookingBarSheet = ({
  cities,
  activeHotelRoutes,
}: {
  cities: City[]
  activeHotelRoutes: ActiveHotelRoutes
}) => {
  const t = useTranslations()

  const datesRef = useRef<HTMLButtonElement>(null)
  const guestRef = useRef<HTMLButtonElement>(null)

  const pathname = usePathname()

  const [bookingUrl, setBookingUrl] = useState<string | null>(null)
  const [variant, setVariant] = useState<'open' | 'closed'>('closed')
  const [openCombo, setOpenCombo] = useState(false)
  const [promoOpen, setPromoOpen] = useState(false)
  const { openDatePicker, setOpenDatePicker, citySlug, countrySlug, filter } = useBookingBarStore()

  // Includes params that are pushed but not yet committed, so Search never uses a stale URL
  const { params: searchParams } = useBookingParams()
  const {
    fromDateParam: from,
    toDateParam: to,
    synxisId,
    adult,
    child,
    rooms,
    promo,
  } = useBookingSearchParams()

  const [date, setDate] = useState<DateRange | undefined>(
    from && to
      ? {
          from: parse(from, 'yyyy-MM-dd', new Date()),
          to: parse(to, 'yyyy-MM-dd', new Date()),
        }
      : undefined,
  )

  // Keep dates in sync with the URL, the bar stays mounted across navigations
  useEffect(() => {
    setDate(
      from && to
        ? {
            from: parse(from, 'yyyy-MM-dd', new Date()),
            to: parse(to, 'yyyy-MM-dd', new Date()),
          }
        : undefined,
    )
  }, [from, to])

  const activeHotel = synxisId && from && to ? activeHotelRoutes[synxisId] : undefined

  const hotelHref = activeHotel && {
    pathname: '/hotels/[country]/[city]/[hotel]' as const,
    params: activeHotel,
    query: Object.fromEntries(searchParams),
  }

  useEffect(() => {
    if (!synxisId || !from || !to || activeHotelRoutes[synxisId]) {
      setBookingUrl(null)
      return
    }

    setBookingUrl(`https://be.synxis.com?chain=30580&${searchParams}`)
  }, [searchParams])

  function handleBookingTriggers(type: 'search' | 'guests' | 'dates') {
    setPromoOpen(true)
    switch (type) {
      case 'dates':
        if (!date) {
          datesRef.current?.click()
        }
        break
      case 'guests':
        if (!adult || !rooms) {
          guestRef.current?.click()
        }
        break
    }
  }

  // Check if we're on the hotel detail page pattern
  const isHotelDetailPage =
    /^\/(?:(en|da|se|no)\/)?(hotels|hoteller|hotell)\/[^\/]+\/[^\/]+\/[^\/]+\/?$/.test(pathname)

  // Press listing and press article pages (/press, /presse, /press/[slug], /presse/[slug])
  const isPressPage = /^\/(?:(en|da|se|no)\/)?(press|presse)(?:\/[^\/]+)?\/?$/.test(pathname)

  const normalizedPathname = pathname.split('/').pop()
  const hidePriceGuarantee = pathname.includes('booking')
  const isBlacklisted = ['complete-user', 'checkout', 'confirmation'].includes(
    normalizedPathname ?? '',
  )

  const isMobile = useIsMobile()

  // Don't render on blacklisted routes
  if (isHotelDetailPage || isBlacklisted || isPressPage) {
    return null
  }

  let filteredCities = cities
  if (filter != null) {
    filteredCities = filteredCities
      .map((city) => {
        if (!city.hotels?.docs) return null

        const c = {
          ...city,
          hotels: {
            docs: city.hotels.docs.filter((h) => filter?.includes(typeof h == 'number' ? h : h.id)),
          },
        }

        if (!c.hotels.docs.length) return null
        return c as City
      })
      .filter((city) => city) as City[]
  }

  return (
    <>
      {isMobile ? (
        <>
          <div
            data-state={variant}
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-30 bg-black/50 backdrop-blur-sm duration-200',
              'data-[state=closed]:invisible data-[state=open]:visible',
              variant === 'closed' ? 'pointer-events-none' : 'pointer-events-auto',
            )}
            onClick={() => setVariant('closed')}
          />

          <div className='fixed bottom-1 left-0 z-30 flex w-full items-center md:hidden'>
            <div className='absolute bottom-0 left-0 z-30 w-full p-4'>
              {variant === 'open' && hotelHref ? (
                <Link
                  href={hotelHref}
                  onClick={() => setVariant('closed')}
                  className={cn(
                    'bg-primary text-primary-foreground inline-flex h-14 w-full items-center justify-center rounded-full text-lg',
                  )}
                >
                  {t('find_your_stay')}
                </Link>
              ) : variant === 'open' && bookingUrl ? (
                <a
                  href={bookingUrl}
                  target='_blank'
                  className={cn(
                    'bg-primary text-primary-foreground inline-flex h-14 w-full items-center justify-center rounded-full text-lg',
                  )}
                >
                  {t('find_your_stay')}
                </a>
              ) : citySlug && countrySlug && variant == 'open' ? (
                <Link
                  href={{
                    pathname: '/hotels/[country]/[city]',
                    params: { country: countrySlug, city: citySlug },
                    query: Object.fromEntries(searchParams),
                  }}
                  onClick={() => setVariant('closed')}
                  className={cn(
                    'bg-primary text-primary-foreground inline-flex h-14 w-full items-center justify-center rounded-full text-lg',
                  )}
                >
                  {t('find_your_stay')}
                </Link>
              ) : (
                <Button
                  className='h-14 w-full'
                  size={'lg'}
                  onClick={() => {
                    if (!synxisId && variant === 'open') {
                      setOpenCombo(true)
                    } else if (variant === 'closed') {
                      setVariant('open')
                    }
                  }}
                >
                  {t('find_your_stay')}
                </Button>
              )}
            </div>

            {!hidePriceGuarantee && (
              <Link href={'/pricematch'} onClick={() => setVariant('closed')}>
                <div
                  className={`bg-icon border-secondary absolute -top-[32px] left-4 flex h-10 items-center space-x-2 rounded-t-lg border border-b-0 pr-4 pl-3 transition-opacity duration-300 ease-out ${variant === 'open' ? 'opacity-100' : 'opacity-0'}`}
                >
                  <Coins className='text-icon-foreground size-5' strokeWidth={1.5} />

                  <p>{t('best_price_guarantee')}</p>
                </div>
              </Link>
            )}

            <div
              className={`animated-div h-14 w-full rounded-full border bg-stone-100 ${variant === 'open' ? 'open' : 'closed'}`}
            >
              <div className='content flex flex-col space-y-2 p-2'>
                <SearchCombobox
                  open={openCombo}
                  setOpen={setOpenCombo}
                  data={filteredCities}
                  handleBookingTriggers={handleBookingTriggers}
                  hotelId={synxisId}
                  placeholder={t('search_cities_and_hotels')}
                />

                <DatePickerWithRange
                  date={date}
                  setDate={setDate}
                  open={openDatePicker}
                  setOpen={setOpenDatePicker}
                  handleBookingTriggers={handleBookingTriggers}
                >
                  <button
                    ref={datesRef}
                    className='bg-input border-secondary flex w-full items-center gap-2 rounded-md border px-2 py-3'
                  >
                    <div
                      className={cn('flex h-full w-1/2 items-center space-x-2', {
                        'text-muted-foreground': !date?.from,
                      })}
                    >
                      <CalendarCheck2 className='size-5' strokeWidth={1.5} />
                      <p>{date?.from ? format(date.from, 'dd.MM.yy') : t('check_in')}</p>
                    </div>
                    <span className='bg-border h-full w-[1px] rounded-full' />
                    <div
                      className={cn('flex h-full w-1/2 items-center space-x-2', {
                        'text-muted-foreground': !date?.to,
                      })}
                    >
                      <CalendarX2 className='size-5' strokeWidth={1.5} />
                      <p>{date?.to ? format(date.to, 'dd.MM.yy') : t('check_out')}</p>
                    </div>
                  </button>
                </DatePickerWithRange>

                <GuestSelector
                  rooms={rooms}
                  adults={adult}
                  child={child}
                  triggerRef={guestRef}
                  touched={adult || rooms ? true : false}
                />

                <div className='py-2'>
                  <PromoCodeInput />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className='fixed bottom-12 left-0 z-10 hidden w-full px-6 md:block'>
          <div className='relative mx-auto w-full max-w-5xl'>
            {!hidePriceGuarantee && (
              <Link
                href={'/pricematch'}
                className='w-full transition-all hover:scale-110 hover:cursor-pointer'
              >
                <div className='animate-slide-in-top bg-icon animate-in border-secondary absolute top-0 left-4 z-0 flex h-10 origin-bottom-left items-center space-x-2 rounded-t-lg border border-b-0 pr-4 pl-3 transition-all hover:scale-105'>
                  <Coins className='text-icon-foreground size-5' strokeWidth={1.5} />

                  <p>{t('best_price_guarantee')}</p>
                </div>
              </Link>
            )}

            <div className='z-10 flex w-full flex-col items-center justify-between rounded-2xl bg-white px-6 py-4 drop-shadow-md'>
              <div className='grid w-full grid-cols-12 gap-6'>
                <p className='col-span-3'>{t('where_to')}</p>
                <p className='col-span-4'>{t('check_in_out')}</p>
                <p className='col-span-3'>{t('guests')}</p>
              </div>

              <div className='grid w-full grid-cols-12 gap-6'>
                <div className='col-span-3'>
                  <SearchCombobox
                    open={openCombo}
                    setOpen={setOpenCombo}
                    handleBookingTriggers={handleBookingTriggers}
                    data={filteredCities}
                    hotelId={synxisId}
                    placeholder={
                      filter
                        ? t('search_for_hotels_in_this_campaign')
                        : t('search_cities_and_hotels')
                    }
                  />
                </div>
                <div className='col-span-4'>
                  <DatePickerWithRange
                    date={date}
                    setDate={setDate}
                    // open={open}
                    // setOpen={setOpen}
                    open={openDatePicker}
                    setOpen={setOpenDatePicker}
                    handleBookingTriggers={handleBookingTriggers}
                  >
                    <button
                      ref={datesRef}
                      className='bg-input border-secondary hover:bg-background relative flex w-full items-center gap-2 rounded-lg border px-2 py-3 shadow-md transition-colors'
                    >
                      <div
                        className={cn('flex h-full w-1/2 items-center space-x-2', {
                          'text-muted-foreground': !date?.from,
                        })}
                      >
                        <CalendarCheck2 className='size-5' strokeWidth={1.5} />
                        <p>{date?.from ? format(date.from, 'dd.MM.yy') : t('check_in')}</p>
                      </div>
                      <span className='bg-border h-full w-[1px] rounded-full' />
                      <div
                        className={cn('flex h-full w-1/2 items-center space-x-2', {
                          'text-muted-foreground': !date?.to,
                        })}
                      >
                        <CalendarX2 className='size-5' strokeWidth={1.5} />
                        <p>{date?.to ? format(date.to, 'dd.MM.yy') : t('check_out')}</p>
                      </div>
                    </button>
                  </DatePickerWithRange>
                </div>
                <div className='col-span-3'>
                  <GuestSelector
                    rooms={rooms}
                    adults={adult}
                    child={child}
                    triggerRef={guestRef}
                    touched={adult || rooms ? true : false}
                  />
                </div>

                {hotelHref ? (
                  <Link
                    href={hotelHref}
                    className={cn(
                      'bg-primary text-primary-foreground col-span-2 inline-flex h-12 w-full items-center justify-center rounded-full text-lg',
                    )}
                  >
                    {t('search')}
                  </Link>
                ) : bookingUrl ? (
                  <a
                    href={bookingUrl}
                    target='_blank'
                    className={cn(
                      'bg-primary text-primary-foreground col-span-2 inline-flex h-12 w-full items-center justify-center rounded-full text-lg',
                    )}
                  >
                    {t('search')}
                  </a>
                ) : citySlug && countrySlug ? (
                  <Link
                    href={{
                      pathname: '/hotels/[country]/[city]',
                      params: { country: countrySlug, city: citySlug },
                      query: Object.fromEntries(searchParams),
                    }}
                    className={cn(
                      'bg-primary text-primary-foreground col-span-2 inline-flex h-12 w-full items-center justify-center rounded-full text-lg',
                    )}
                  >
                    {t('search')}
                  </Link>
                ) : (
                  <Button
                    className='col-span-2 h-12 w-full'
                    size={'lg'}
                    onClick={() => {
                      if (!synxisId) {
                        setOpenCombo(true)
                      } else {
                        setOpenDatePicker(true)
                      }
                    }}
                  >
                    {t('search')}
                  </Button>
                )}
              </div>

              <div
                className={cn('flex w-full justify-end pt-2', {
                  hidden: !promoOpen && !promo,
                  flex: synxisId && from && to && rooms,
                })}
              >
                <PromoCodeInput />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default BookingBarSheet
